import {ModelSetOptions} from 'backbone';

import {Alignment} from 'analyses/Analyses';
import {Sequence} from 'components/Sequence';
import Protein from 'molbio/proteins/Protein';
import {PolymerTabState, PolymerTabStateAttributes} from 'molbio/tabstates/PolymerTabState';
import {TabStateDefaults} from 'Navigation';
import {MakeLocalSyncOptions, VisibilityOptions} from 'settings/visibility-options/VisibilityOptions';

export interface PolymerMapTabStateAttributes<Polymer extends Sequence | Protein>
  extends PolymerTabStateAttributes<Polymer> {
  visibilityOptions: null | VisibilityOptions;
}

interface PolymerMapTabState<Polymer extends Sequence | Protein> {
  get<K extends keyof PolymerMapTabStateAttributes<Polymer>>(
    key: K
  ): PolymerMapTabStateAttributes<Polymer>[K];
  set<K extends keyof PolymerMapTabStateAttributes<Polymer>>(
    attributeName: K,
    value: PolymerMapTabStateAttributes<Polymer>[K],
    options?: ModelSetOptions
  ): PolymerMapTabState<Polymer>;
  set(obj: Partial<PolymerMapTabStateAttributes<Polymer>>): PolymerMapTabState<Polymer>;
}

/**
 * Abstract base class model for a map backed by a polymer, either Sequence or Protein.
 *
 * It extends the PolymerTabState by adding in VisibilityOptions, which are only applicable
 * to map tabs.
 */
abstract class PolymerMapTabState<Polymer extends Sequence | Protein> extends PolymerTabState<Polymer> {
  initialize(attributes?: {}, options?: {}): void {
    super.initialize(attributes, options);
    this.listenTo(this.polymer(), 'toggleOption', (option: string, visible?: boolean) => {
      if (visible == null) {
        visible = true;
      }
      this.toggleOption(option, visible);
    });
  }

  toggleOption(option: string, visible: boolean): void {
    const visibilityOptions = this.get('visibilityOptions');
    if (visibilityOptions != null) {
      visibilityOptions.getOption(option)?.set({visible});
    }
  }

  abstract msVisibilityOptions(context?: MakeLocalSyncOptions | Alignment): VisibilityOptions;

  defaults(): TabStateDefaults & Partial<PolymerMapTabStateAttributes<Polymer>> {
    return {
      ...super.defaults(),
      visibilityOptions: null,
    };
  }
}

export default PolymerMapTabState;
